import {
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Calendar as CalendarIcon,
} from "lucide-react";
import {
  format,
  addMonths,
  startOfMonth,
} from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  parseISODateString,
  toISODateString,
} from "@/lib/formatters";

const DatePickerField = ({
  value,
  onChange,
  placeholder = "Select date",
  testId,
  disabled = false,
}) => {
  const [open, setOpen] =
    useState(false);

  const selectedDate =
    value
      ? parseISODateString(value)
      : null;

  const [currentMonth, setCurrentMonth] =
    useState(
      startOfMonth(
        selectedDate || new Date()
      )
    );

  const touchStartY =
    useRef(null);

  const touchStartX =
    useRef(null);

  const wheelLocked =
    useRef(false);

  useEffect(() => {
    if (!open) return;

    setCurrentMonth(
      startOfMonth(
        selectedDate ||
          new Date()
      )
    );
  }, [open, value]);

  const moveMonth = (
    direction
  ) => {
    setCurrentMonth(
      (month) =>
        addMonths(
          month,
          direction
        )
    );
  };

  const handleWheel = (
    e
  ) => {
    if (
      Math.abs(e.deltaY) <
      8
    ) {
      return;
    }

    if (
      wheelLocked.current
    ) {
      return;
    }

    wheelLocked.current =
      true;

    if (e.deltaY > 0) {
      moveMonth(1);
    } else {
      moveMonth(-1);
    }

    window.setTimeout(
      () => {
        wheelLocked.current =
          false;
      },
      220
    );
  };

  const handleTouchStart = (
    e
  ) => {
    const touch =
      e.touches?.[0];

    if (!touch) return;

    touchStartY.current =
      touch.clientY;

    touchStartX.current =
      touch.clientX;
  };

  const handleTouchEnd = (
    e
  ) => {
    const touch =
      e.changedTouches?.[0];

    if (
      !touch ||
      touchStartY.current ==
        null ||
      touchStartX.current ==
        null
    ) {
      return;
    }

    const diffY =
      touchStartY.current -
      touch.clientY;

    const diffX =
      touchStartX.current -
      touch.clientX;

    touchStartY.current =
      null;

    touchStartX.current =
      null;

    const minimumSwipe =
      45;

    if (
      Math.abs(diffY) <
      minimumSwipe
    ) {
      return;
    }

    if (
      Math.abs(diffX) >
      Math.abs(diffY)
    ) {
      return;
    }

    if (diffY > 0) {
      moveMonth(1);
    } else {
      moveMonth(-1);
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={
        setOpen
      }
    >
      <PopoverTrigger
        asChild
      >
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={
            testId
          }
          className={`w-full h-12 justify-start text-left font-normal bg-slate-50 border border-slate-200 hover:border-[#fa9d1c] hover:bg-slate-50 rounded-xl px-4 text-base ${
            !selectedDate
              ? "text-slate-400"
              : "text-slate-900"
          }`}
        >
          <CalendarIcon className="w-5 h-5 mr-2 text-slate-500" />

          {selectedDate
            ? format(
                selectedDate,
                "dd MMM yyyy"
              )
            : placeholder}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0 bg-white border border-slate-200"
        align="start"
      >
        <div
          onWheel={
            handleWheel
          }
          onTouchStart={
            handleTouchStart
          }
          onTouchEnd={
            handleTouchEnd
          }
          className="touch-pan-y"
        >
          <Calendar
            mode="single"
            selected={
              selectedDate
            }
            month={
              currentMonth
            }
            onMonthChange={
              setCurrentMonth
            }
            onSelect={(
              date
            ) => {
              if (date) {
                onChange(
                  toISODateString(
                    date
                  )
                );

                setOpen(
                  false
                );
              }
            }}
            initialFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerField;
